import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useInterruptedMeasurementRecovery } from '../hooks/useInterruptedMeasurementRecovery';

export interface MeasurementInterruptedModalProps {
  /** Which measurement phase was active when interrupted */
  phase: 'baseline' | 'specimen';
  /** Called when user chooses Resume after successful recovery */
  onResume: (outcome: 'harvested' | 'still-running') => void;
  /** Called when user chooses Discard */
  onDiscard: () => void;
}

function formatAge(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
}

function PhaseLabel({ phase }: { phase: 'baseline' | 'specimen' }) {
  return <strong>{phase}</strong>;
}

export function MeasurementInterruptedModal({
  phase,
  onResume,
  onDiscard,
}: MeasurementInterruptedModalProps) {
  const {
    modalState,
    error,
    isConnecting,
    interruptedAge,
    handleTryAgain,
    handleRetryProbe,
    handleResume,
    handleContinueResume,
    handleAbortMeasurement,
    handleDiscard,
  } = useInterruptedMeasurementRecovery({ phase, onResume, onDiscard });

  return (
    <Dialog
      open
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      aria-modal="true"
      aria-label="Measurement Interrupted"
      onClose={() => {}}
    >
      <DialogContent>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {/* Icon */}
          <Box sx={{ mb: 2 }}>
            {modalState === 'recovered' && (
              <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main' }} />
            )}
            {(modalState === 'lost' || modalState === 'reconnect-failed') && (
              <ErrorOutlineIcon sx={{ fontSize: 56, color: 'error.main' }} />
            )}
            {modalState === 'stale' && (
              <WifiOffIcon sx={{ fontSize: 56, color: 'warning.main' }} />
            )}
            {modalState === 'resuming' && (
              <PlayCircleOutlineIcon sx={{ fontSize: 56, color: 'info.main' }} />
            )}
            {isConnecting && <CircularProgress size={56} />}
          </Box>

          {/* Title */}
          <Typography variant="h6" gutterBottom>
            {modalState === 'stale' && 'Stale Measurement Found'}
            {modalState === 'auto-reconnecting' && 'Reconnecting to Reader\u2026'}
            {modalState === 'probing' && 'Checking Reader Status\u2026'}
            {modalState === 'harvesting' && 'Saving Measurement\u2026'}
            {modalState === 'recovered' && 'Measurement Recovered'}
            {modalState === 'resuming' && 'Measurement Resuming'}
            {modalState === 'lost' && 'Measurement Lost'}
            {modalState === 'reconnect-failed' && 'Could Not Reconnect'}
          </Typography>

          {/* Body */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {modalState === 'stale' && (
              <>
                A <PhaseLabel phase={phase} /> measurement was interrupted{' '}
                {interruptedAge !== null ? formatAge(interruptedAge) : ''}. The measurement data
                may no longer be available on the reader.
              </>
            )}
            {modalState === 'auto-reconnecting' &&
              'Please keep the reader powered on and nearby.'}
            {modalState === 'probing' && 'Querying the reader for measurement data.'}
            {modalState === 'harvesting' && 'Saving your measurement result\u2026'}
            {modalState === 'recovered' && (
              <>
                Your <PhaseLabel phase={phase} /> measurement has been saved successfully.
              </>
            )}
            {modalState === 'resuming' &&
              'The reader is still running your measurement. Reconnecting to the live stream\u2026'}
            {modalState === 'lost' && error}
            {modalState === 'reconnect-failed' &&
              'Make sure the reader is powered on and connected, then try again.'}
          </Typography>

          {/* Error detail */}
          {(modalState === 'lost' || modalState === 'reconnect-failed') && error && (
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {error}
            </Alert>
          )}

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {modalState === 'stale' && (
              <>
                <Button variant="outlined" onClick={handleTryAgain}>
                  Try Recovery Anyway
                </Button>
                <Button variant="contained" color="error" onClick={handleDiscard}>
                  Discard Measurement
                </Button>
              </>
            )}

            {modalState === 'reconnect-failed' && (
              <>
                <Button variant="contained" onClick={handleTryAgain}>
                  Try Again
                </Button>
                <Button variant="outlined" color="inherit" onClick={handleDiscard}>
                  Discard Measurement
                </Button>
              </>
            )}

            {modalState === 'lost' && (
              <>
                <Button variant="contained" onClick={handleRetryProbe}>
                  Try Again
                </Button>
                <Button variant="outlined" color="inherit" onClick={handleDiscard}>
                  Discard Measurement
                </Button>
              </>
            )}

            {modalState === 'resuming' && (
              <>
                <Button variant="contained" onClick={handleContinueResume}>
                  Continue Measurement
                </Button>
                <Button variant="outlined" color="error" onClick={handleAbortMeasurement}>
                  Abort Measurement
                </Button>
              </>
            )}

            {modalState === 'recovered' && (
              <Button variant="contained" onClick={handleResume}>
                Resume
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
